import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Tooltip, Typography } from '@mui/material';
import { Delete, Info } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import EditableTable from '../elements/EditableTable';
import AddForm from './AddForm';
import EntityDeleteConfirmation from './EntityDeleteConfirmation';
import AutomationCredentials from '../automation/AutomationCredentials';
import AutomationIntegration from '../automation/AutomationIntegration';
import ExecutionScope from './config/ExecutionScope';
import Integrations from '../integrations';

import {
  getConfigurations,
  addEntity,
  deleteEntity,
  updateEntity,
} from '../../modules/config';
import { getConfigDetails, getTableHeader } from './tableData';
import { useNotification } from '../../hooks/NotificationContext';
import CustomIconButton from '../elements/CustomIconButton';
import CustomFieldsComponent from './CustomFieldsComponent';

const Configurations = ({ selectedTab, user }) => {
  const { projectId } = useParams();
  const { heading, group } = getConfigDetails(selectedTab);
  const { addNotification } = useNotification();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  const isAdmin = ['org-admin', 'org-manager', 'admin'].includes(user?.role);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConfigurations(selectedTab, projectId);
      if (response) {
        setData(response.data);
      }
    } catch (error) {
      addNotification('danger', error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, projectId, addNotification]);

  useEffect(() => {
    if (!['api-token', 'integration', 'scope', 'jira', 'automationfields'].includes(selectedTab)) {
      fetchData();
    }
  }, [selectedTab, fetchData]);

  const handleUpdateEntity = async (id, editingValues) => {
    try {
      await updateEntity(id, { entity: editingValues });
      addNotification('success', 'Updated successfully');
      fetchData();
    } catch (error) {
      addNotification('danger', error.message);
    }
  };

  const handleDeleteClick = (id) => {
    setEntityToDelete(id);
    setShowConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    setLoadingDelete(true);
    try {
      await deleteEntity(entityToDelete);
      addNotification('success', 'Deleted successfully');
      fetchData();
    } catch (error) {
      addNotification('danger', error.message);
    } finally {
      setLoadingDelete(false);
      handleCloseModal();
    }
  };

  const handleAddNewEntity = async (formData) => {
    setLoading(true);
    try {
      await addEntity({ entity: { ...formData, group } }, projectId);
      addNotification('success', 'Added successfully');
      fetchData();
    } catch (error) {
      addNotification(
        'danger',
        error.message || 'Failed to create. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmationModal(false);
    setEntityToDelete(null);
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'api-token':
      case 'integration':
        return selectedTab === 'api-token' ? (
          <Grid item xs={12}>
            <AutomationCredentials selectedTab={selectedTab} />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <AutomationIntegration isAdmin={isAdmin} />
          </Grid>
        );
      case 'scope':
        return (
          <Grid item xs={12}>
            <ExecutionScope isAdmin={isAdmin} />{' '}
          </Grid>
        );
      case 'jira':
        return (
          <Grid item xs={12}>
            <Integrations isAdmin={isAdmin} />
          </Grid>
        );
      case 'automationfields':
        return (
          <Grid item xs={12}>
            <CustomFieldsComponent isAdmin={isAdmin} selectedTab={selectedTab} />
          </Grid>
        )
      default:
        return (
          <>
            {isAdmin && (
              <Grid item xs={12}>
                <AddForm
                  entityType={selectedTab}
                  group={group}
                  handleSubmit={handleAddNewEntity}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <EditableTable
                data={data}
                type={selectedTab}
                headerTabs={getTableHeader(selectedTab)}
                order="asc"
                isAdmin={isAdmin}
                isLoading={loading}
                setLoading={setLoading}
                handleUpdateEntity={handleUpdateEntity}
                fetchData={fetchData}
                editableFields={['name', 'description']}
                action={(row) =>
                  row.gainss_project_id === null ? (
                    <Tooltip title={"Can't be modified"} arrow classes={{ popper: "custom-tooltip" }}
                      sx={{ cursor: 'default', padding: '10px' }}>
                      <Info sx={{ color: 'info.main' }} />
                    </Tooltip>
                  ) : (
                    <CustomIconButton
                      title="Delete"
                      disableRipple
                      onClick={() => handleDeleteClick(row.id)}
                      disabled={row.is_default || loadingDelete}>
                      <Delete sx={{ color: "red" }} />
                    </CustomIconButton>
                  )
                }
              />
              <EntityDeleteConfirmation
                show={showConfirmationModal}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirmDelete}
                entityToDelete={entityToDelete}
              />
            </Grid>
          </>
        );
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h4" className="mb-2">
          {heading || 'Configurations'}
        </Typography>
      </Grid>
      {renderTabContent()}
    </Grid>
  );
};

export default Configurations;
